// The Skills tab (#5211): the sidebar's groups and label filter, where a skill came from,
// Discover's picks and the top search's Skills rows. Pure: the components only render these.
import type { MarketplaceSkill, Skill } from "$lib/plugins/agents/types.js";
import { importedSkillFor } from "./skills-marketplace-view-model.js";
import { humanizeSkillName } from "./skill-upload.js";

type SkillRowLike = Pick<Skill, "id" | "name" | "slug" | "sourceUrl" | "labels">;

// ─── Sidebar ─────────────────────────────────────────────────────────────────

export interface SidebarGroups<T> {
  written: T[];
  marketplace: T[];
}

export function sidebarGroups<T extends { sourceUrl?: string }>(
  skills: readonly T[],
): SidebarGroups<T> {
  return {
    written: skills.filter((s) => !s.sourceUrl),
    marketplace: skills.filter((s) => !!s.sourceUrl),
  };
}

export interface LabelCount {
  label: string;
  count: number;
}

export function labelCounts(skills: readonly Pick<Skill, "labels">[]): LabelCount[] {
  const counts = new Map<string, number>();
  for (const s of skills) for (const l of s.labels ?? []) counts.set(l, (counts.get(l) ?? 0) + 1);
  return [...counts]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** A skill with ANY of the picked labels; no pick keeps every skill. */
export function filterByLabels<T extends Pick<Skill, "labels">>(
  skills: readonly T[],
  picked: readonly string[],
): T[] {
  if (picked.length === 0) return [...skills];
  return skills.filter((s) => (s.labels ?? []).some((l) => picked.includes(l)));
}

export interface AgentFace {
  id: string;
  name: string;
  avatar: string;
}

export interface FaceStack<T> {
  shown: T[];
  extra: number;
}

export function faceStack<T>(items: readonly T[], max: number): FaceStack<T> {
  return { shown: items.slice(0, max), extra: Math.max(0, items.length - max) };
}

/** "Yoshi, Reviewer, Floema and 9 more": up to `shown` names, the rest counted. */
export function agentNamesLine(names: readonly string[], shown = 3): string {
  if (names.length === 0) return "No agents";
  if (names.length <= shown) {
    if (names.length === 1) return names[0];
    return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
  }
  return `${names.slice(0, shown).join(", ")} and ${names.length - shown} more`;
}

// ─── Where a skill came from ────────────────────────────────────────────────

const KNOWN_OWNERS: Record<string, string> = { anthropics: "Anthropic" };

export interface SkillOrigin {
  // "github": imported from a repository, whichever marketplace listed it.
  kind: "workspace" | "github";
  owner?: string;
  repo?: string;
}

export function skillOrigin(skill: Pick<Skill, "sourceUrl">): SkillOrigin {
  if (!skill.sourceUrl) return { kind: "workspace" };
  if (!URL.canParse(skill.sourceUrl)) return { kind: "github" };
  const url = new URL(skill.sourceUrl);
  const [owner, repo] = url.pathname.split("/").filter(Boolean);
  if (!owner || !/(^|\.)github(usercontent)?\.com$/.test(url.hostname)) return { kind: "github" };
  return {
    kind: "github",
    owner: KNOWN_OWNERS[owner] ?? owner,
    ...(repo ? { repo: `${owner}/${repo}` } : {}),
  };
}

/** Who a skill is by: an import's owner, else the member who added it (addedBy is a user id, never shown). */
export function skillAuthor(
  skill: Pick<Skill, "sourceUrl" | "addedBy">,
  memberName: (userId: string) => string | null,
): string | undefined {
  return skillOrigin(skill).owner ?? ((skill.addedBy && memberName(skill.addedBy)) || undefined);
}

/** A marketplace row's author: "Anthropic" for anthropics/skills, else the repo owner. */
export function marketplaceOwner(result: Pick<MarketplaceSkill, "repo">): string {
  const owner = result.repo.split("/")[0] ?? result.repo;
  return KNOWN_OWNERS[owner] ?? owner;
}

// ─── Discover ────────────────────────────────────────────────────────────────

export type DiscoverRequest =
  | { kind: "marketplace"; result: MarketplaceSkill }
  | { kind: "search"; query: string };

export function addable<T extends { id: string; sourceUrl?: string }>(
  results: readonly MarketplaceSkill[],
  skills: readonly T[],
): MarketplaceSkill[] {
  return results.filter((r) => !!r.importUrl && !importedSkillFor(r, skills));
}

/** The featured skill: the most installed official one not yet added, else the most installed. */
export function featuredPick<T extends { id: string; sourceUrl?: string }>(
  mostInstalled: readonly MarketplaceSkill[],
  skills: readonly T[],
): MarketplaceSkill | undefined {
  const open = addable(mostInstalled, skills);
  return open.find((r) => r.source === "anthropic" || r.alsoIn.includes("anthropic")) ?? open[0];
}

export interface AgentLike {
  id: string;
  name: string;
  role?: string | null;
}

export interface RecommendationQuery {
  query: string;
  agents: string[];
}

const ROLE_STOPWORDS = new Set(["a", "an", "the", "and", "or", "of", "for", "to", "in", "on", "with", "who", "that", "your", "our"]); // prettier-ignore

/** An agent's role as a marketplace query: its first three meaningful words. */
export function roleQuery(role: string | null | undefined): string | undefined {
  const words = (role ?? "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]+/gu, " ")
    .split(/\s+/)
    .filter((w) => w.length > 1 && !ROLE_STOPWORDS.has(w));
  const query = words.slice(0, 3).join(" ");
  return query.length >= 2 ? query : undefined;
}

// One query per distinct role, the agents holding the fewest skills first, at most `max`.
export function recommendationQueries(
  agents: readonly AgentLike[],
  skills: readonly Pick<Skill, "assignments">[],
  max = 3,
): RecommendationQuery[] {
  const held = (id: string) =>
    skills.filter((s) => s.assignments.some((a) => a.cyboId === id)).length;
  const ranked = agents
    .map((agent, index) => ({ agent, index, query: roleQuery(agent.role), held: held(agent.id) }))
    .filter((a): a is typeof a & { query: string } => a.query !== undefined)
    .sort((a, b) => a.held - b.held || a.index - b.index);
  const out: RecommendationQuery[] = [];
  for (const a of ranked) {
    const same = out.find((q) => q.query === a.query);
    if (same) same.agents.push(a.agent.name);
    else if (out.length < max) out.push({ query: a.query, agents: [a.agent.name] });
  }
  return out;
}

export interface Recommendation {
  result: MarketplaceSkill;
  forAgents: string[];
}

// Each query's best addable rows; a skill found for several queries names every agent it fits.
export function pickRecommendations<T extends { id: string; sourceUrl?: string }>(
  answers: ReadonlyArray<{ query: RecommendationQuery; results: readonly MarketplaceSkill[] }>,
  skills: readonly T[],
  perQuery = 2,
): Recommendation[] {
  const out: Recommendation[] = [];
  for (const { query, results } of answers) {
    for (const result of addable(results, skills).slice(0, perQuery)) {
      const same = out.find((r) => r.result.id === result.id);
      if (same) same.forAgents.push(...query.agents.filter((a) => !same.forAgents.includes(a)));
      else out.push({ result, forAgents: [...query.agents] });
    }
  }
  return out;
}

export function forAgentsLabel(names: readonly string[]): string {
  return `For ${agentNamesLine(names, 2)}`;
}

// ─── The top search's Skills section ────────────────────────────────────────

export type SkillSearchHit =
  | { kind: "workspace"; skill: Skill; score: number }
  | { kind: "marketplace"; result: MarketplaceSkill };

// -1 no match; a prefix of the name beats a word start, which beats any other match.
function scoreOf(skill: SkillRowLike & { description?: string }, q: string): number {
  const name = humanizeSkillName(skill.name || skill.slug).toLowerCase();
  const fields = [
    name,
    skill.slug.toLowerCase(),
    (skill.description ?? "").toLowerCase(),
    ...(skill.labels ?? []),
  ];
  if (name.startsWith(q) || skill.slug.startsWith(q)) return 3;
  if (fields.some((f) => new RegExp(`(^|[\\s\\-_/.])${escapeRegExp(q)}`).test(f))) return 2;
  return fields.some((f) => f.includes(q)) ? 1 : -1;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function workspaceSkillHits(skills: readonly Skill[], query: string): SkillSearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return skills
    .map((skill) => ({ kind: "workspace" as const, skill, score: scoreOf(skill, q) }))
    .filter((h) => h.score >= 0)
    .sort((a, b) => b.score - a.score);
}

export function skillSearchHits(
  skills: readonly Skill[],
  query: string,
  marketplace: readonly MarketplaceSkill[],
): SkillSearchHit[] {
  const own = workspaceSkillHits(skills, query);
  const fresh = marketplace.filter((r) => !importedSkillFor(r, skills));
  return [...own, ...fresh.map((result) => ({ kind: "marketplace" as const, result }))];
}
